import { Badge, Box, Flex, HStack, Image, VStack } from "@chakra-ui/react";
import type { DTO } from "@equidefi/shared/types/dto";
import {
  formatSecurities,
  getInvestmentDisplayStatus,
  INVESTMENT_STATUS_COLORS,
  type InvestmentDisplayStatus,
  offeringHidesSecurityLabel,
  offeringIsTerminated,
} from "@equidefi/shared";
import { Heading, Text } from "@equidefi/ui";
import React, { useMemo } from "react";
import { CurrencyFormatter } from "../../../constants/format";
import Continue from "../continue";

interface StatusBadgeProps {
  action: InvestmentDisplayStatus;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ action }) => {
  return (
    <Badge
      h="6"
      lineHeight="6"
      fontSize="14px"
      justifyContent="center"
      bgColor={INVESTMENT_STATUS_COLORS[action]}
      w="full"
      textAlign="center">
      {action}
    </Badge>
  );
};

export interface InvestmentCardProps {
  investment: DTO.Investment | null | undefined;
  issuer?: DTO.Issuer | null;
  offering?: DTO.Offering | null;
  cardAction?: () => void;
  hideStatusBadge?: boolean;
  hideResumeButton?: boolean;
}

/**
 * InvestmentCard - Enhanced investment card with improved status logic
 *
 * Status badge logic aligned with progress display phases:
 * - PENDING status → INCOMPLETE badge (Yellow)
 * - APPROVED/REVIEW status → PROCESSING badge (Dark Gray)
 * - CLOSED status → COMPLETED badge (Green)
 * - CANCELED status → CANCELED badge (Red)
 */
export const InvestmentCard: React.FC<InvestmentCardProps> = ({
  investment,
  issuer,
  offering,
  cardAction,
  hideStatusBadge = false,
  hideResumeButton = false,
}) => {
  if (!investment) {
    return null;
  }

  const issuerData = issuer ?? investment.issuer;
  const offeringData = offering ?? investment.offering;
  const hideSecurityLabel = offeringHidesSecurityLabel(offeringData);

  const isOfferingTerminated = offeringIsTerminated(offeringData);

  const status = useMemo(() => getInvestmentDisplayStatus(investment), [investment]);

  return (
    <Box
      w="full"
      h="auto"
      borderRadius="md"
      bg="linear-gradient(150deg, black 0%, #3e3e3e 100%)"
      p="4"
      color="white"
      onClick={cardAction}
      cursor={cardAction ? "pointer" : undefined}>
      {!hideStatusBadge && <StatusBadge action={status} />}

      <VStack gap="2.5" minW="full" mt="3">
        <Flex minW="full">
          <Image
            mr="2.5"
            mb="0"
            w="50px"
            h="50px"
            borderRadius="4px"
            bg="white"
            objectFit="contain"
            p="1"
            src={issuerData?.photo_url}
            alt={`${issuerData?.name || "Issuer"} logo`}
          />
          <Box>
            <Heading mb="0" textStyle="h3" fontWeight="bold">
              {issuerData?.name}
            </Heading>
            <HStack>
              <Text m="0" textStyle="h2" fontWeight="bold">
                {CurrencyFormatter.format(investment.amount)}
              </Text>
              {!hideSecurityLabel && (
                <Text m="0">
                  {" • "}
                  {formatSecurities({
                    quantity: investment.quantity,
                    offering: offeringData,
                  }).toString()}
                </Text>
              )}
            </HStack>
          </Box>
        </Flex>

        <Text mb="0" ml={{ base: 1, lg: 0 }} textStyle="body2" alignSelf="start">
          {offeringData?.name}
        </Text>

        {!hideStatusBadge && !hideResumeButton && !isOfferingTerminated && (
          <Continue investment={investment} offering={offeringData} />
        )}
      </VStack>
    </Box>
  );
};
